import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import HeroSection from './components/HeroSection';
import FeaturesSection from './components/FeaturesSection';
import PricingPreview from './components/PricingPreview';
import TrustSection from './components/TrustSection';
import CTASection from './components/CTASection';

const HomeLanding = () => {
  return (
    <>
      <Helmet>
        <title>PitchEngine AI - Transform Your Ideas Into Perfect Prompts</title>
        <meta 
          name="description" 
          content="Harness the power of AI to generate intelligent, categorized prompts for marketing, legal, technical, and creative projects. Get three tailored suggestions with every request." 
        />
        <meta name="keywords" content="AI prompts, prompt generation, marketing prompts, legal prompts, technical prompts, GPT prompts" />
        <meta property="og:title" content="PitchEngine AI - Transform Your Ideas Into Perfect Prompts" />
        <meta property="og:description" content="AI-powered prompt generation platform for professionals. Get intelligent, categorized prompts instantly." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="/home-landing" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          <HeroSection />
          <FeaturesSection />
          <PricingPreview />
          <TrustSection />
          <CTASection />
        </main>

        {/* Footer */}
        <footer className="bg-surface border-t border-border py-12">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {/* Brand */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">P</span>
                  </div>
                  <span className="text-xl font-semibold text-text-primary">
                    PitchEngine AI
                  </span>
                </div>
                <p className="text-text-secondary text-sm">
                  Transform your ideas into perfect prompts with AI-powered generation and intelligent categorization.
                </p>
              </div>

              {/* Product */}
              <div className="space-y-4">
                <h4 className="font-semibold text-text-primary">Product</h4>
                <ul className="space-y-2 text-sm text-text-secondary">
                  <li><a href="/prompt-generator" className="hover:text-text-primary transition-smooth">Prompt Generator</a></li>
                  <li><a href="/pricing-plans" className="hover:text-text-primary transition-smooth">Pricing</a></li>
                  <li><a href="/saved-prompts" className="hover:text-text-primary transition-smooth">Saved Prompts</a></li>
                  <li><a href="#" className="hover:text-text-primary transition-smooth">API Access</a></li>
                </ul>
              </div>

              {/* Company */}
              <div className="space-y-4">
                <h4 className="font-semibold text-text-primary">Company</h4>
                <ul className="space-y-2 text-sm text-text-secondary">
                  <li><a href="#" className="hover:text-text-primary transition-smooth">About Us</a></li>
                  <li><a href="#" className="hover:text-text-primary transition-smooth">Blog</a></li>
                  <li><a href="#" className="hover:text-text-primary transition-smooth">Careers</a></li>
                  <li><a href="#" className="hover:text-text-primary transition-smooth">Contact</a></li>
                </ul>
              </div>

              {/* Support */}
              <div className="space-y-4">
                <h4 className="font-semibold text-text-primary">Support</h4>
                <ul className="space-y-2 text-sm text-text-secondary">
                  <li><a href="#" className="hover:text-text-primary transition-smooth">Help Center</a></li>
                  <li><a href="#" className="hover:text-text-primary transition-smooth">Documentation</a></li>
                  <li><a href="#" className="hover:text-text-primary transition-smooth">Privacy Policy</a></li>
                  <li><a href="#" className="hover:text-text-primary transition-smooth">Terms of Service</a></li>
                </ul>
              </div>
            </div>

            <div className="border-t border-border mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
              <p className="text-text-secondary text-sm">
                © {new Date()?.getFullYear()} PitchEngine AI. All rights reserved.
              </p>
              <div className="flex items-center space-x-6 mt-4 md:mt-0">
                <a href="#" className="text-text-secondary hover:text-text-primary transition-smooth">
                  <span className="sr-only">Twitter</span>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                  </svg>
                </a>
                <a href="#" className="text-text-secondary hover:text-text-primary transition-smooth">
                  <span className="sr-only">LinkedIn</span>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default HomeLanding;